import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { skipToken } from '@reduxjs/toolkit/query';
import { getRtkErrorMessage } from 'api/getRtkErrorMessage';
import DiscardConfirmation, {
  useDiscardConfirmation,
} from 'components/DiscardConfirmation';
import { useObjectIdRouteParam } from 'hooks/useObjectIdRouteParam';
import { validateObjectId } from 'utils/objectId.validation';
import { TAffiliationFilters } from '@timeedit/types';
import { Flex, Result } from '@timeedit/ui-components/antd';
import { useGetConfigQuery } from '../api/authConfig.api';
import AffiliationDrawer from './AffiliationDrawer';
import AffiliationFilter from './AffiliationFilter';
import AffiliationHeader from './AffiliationHeader';
import AffiliationTable from './AffiliationTable';

const AffiliationsPage: FC = () => {
  const authConfigId = useObjectIdRouteParam();
  const [params] = useSearchParams();
  const affiliationId = useMemo(() => {
    const id = params.get('id');
    if (id === 'new') return 'new';
    else if (id && validateObjectId(id)) return id;
    return undefined;
  }, [params]);

  const [filter, setFilter] = useState<{
    searchText?: string;
    filters: TAffiliationFilters;
  }>({
    filters: {},
    searchText: undefined,
  });

  const { currentData, error, isFetching } = useGetConfigQuery(
    authConfigId ? { authConfigId } : skipToken,
    { refetchOnMountOrArgChange: true /* mimic behavior of rest of admin */ }
  );

  const [
    openDiscardDialog,
    onCloseDiscardDialog,
    confirmFn,
    confirmDiscardChanged,
  ] = useDiscardConfirmation();

  // The drawer owns the dirty derivation (it has the form). It writes the
  // current value into this ref so the parent can read it synchronously
  // without going through state + useEffect propagation.
  const isAffiliationDirtyRef = useRef(false);

  const confirmDiscardAffiliationChanges = useCallback(
    (actionFn: () => void) => {
      if (isAffiliationDirtyRef.current) {
        return confirmDiscardChanged(actionFn);
      }
      actionFn();
    },
    [confirmDiscardChanged]
  );

  return (
    <Flex
      vertical
      style={{ height: '100%' }}
      className="inner-content--wrapper"
    >
      <Flex vertical style={{ height: '100%' }}>
        <AffiliationHeader name={currentData?.name} loading={isFetching} />
        {error && (
          <Result
            status="error"
            title="Failed to get authentication config"
            subTitle={getRtkErrorMessage(error)}
          />
        )}
        {!error && !isFetching && (
          <div className="affiliations-page__content">
            <AffiliationFilter filter={filter} onFilterChange={setFilter} />
            <AffiliationTable
              authConfigId={authConfigId}
              filter={filter}
              selectedAffiliationId={affiliationId}
            />
            {affiliationId && authConfigId && (
              <AffiliationDrawer
                affiliationId={affiliationId}
                authConfigId={authConfigId}
                confirmDiscardAffiliationChanges={
                  confirmDiscardAffiliationChanges
                }
                isDirtyRef={isAffiliationDirtyRef}
              />
            )}
            <DiscardConfirmation
              open={openDiscardDialog}
              onClose={onCloseDiscardDialog}
              confirmFn={confirmFn}
            />
          </div>
        )}
      </Flex>
    </Flex>
  );
};

export default AffiliationsPage;
